import { v4 as uuidv4 } from 'uuid'
import { DeliveryEnum } from '../interfaces'
import { taxIdTypeIdFromAbbreviation } from './mappers'
import { normalizePhone } from './shipmentImportUtils'
import {
  composeAccountPhone,
  composeConsigneePhone,
  consigneeCountryCode,
  PHONE_COUNTRY_CODE,
} from './shipmentValidation'
import {
  ShipmentGroup,
  ShipmentPieceRow,
} from '../interfaces/Import/ShipmentGroupInterface'
import { ImportDetailDTO } from '../interfaces/Import/ShipmentBulkUploadApi'

/**
 * Construye el `shipment_list[]` (shape de POST /api/shipment/ y de la importación
 * asíncrona) desde los ShipmentGroup del Review.
 *
 * Extraído de ShipmentBulkUploadReview (deuda TODOS, eng-review E1) como función pura
 * para reusarlo en: el create flow, la validación pre-vuelo (#1) y el mapeo inverso
 * de corrección (Approach A). Una sola fuente evita drift entre code paths.
 *
 * NOTA (lossy): el valor declarado por pieza se colapsa aquí en un agregado
 * (declared_value.amount). El inverso payload→grupo no puede recuperar el valor por
 * pieza individual; ver el mapeo inverso para cómo se reconstruye.
 */

export interface BuildShipmentContext {
  user: any
  businessUnits: any[]
  citiesByBu: any[]
  identificationTypes: any[]
  allowedServices: any[]
  paymentMode: string
  serviceId?: number
  externalManifest?: string
  // Oficinas origen usables del contrato (para resolver el origen POR FILA).
  originBusinessUnits?: any[]
}

export function buildShipmentList(
  gs: ShipmentGroup[],
  ctx: BuildShipmentContext
): { shipment_list: any[] } {
  const {
    user,
    businessUnits,
    citiesByBu,
    identificationTypes,
    allowedServices,
    paymentMode,
    serviceId,
    externalManifest,
    originBusinessUnits = [],
  } = ctx
  const fallbackService = serviceId ?? allowedServices[0]?.serviceID ?? 60
  const externalManifestValue = externalManifest?.trim() || null

  // Origen POR FILA: (1) si la fila trae originStoreCode, la BU de ese buCode;
  // (2) si el contrato tiene 1 sola oficina, esa; (3) fallback al cliente.
  // [F2] Si originStoreCode está seteado pero NO resuelve, devolvemos undefined
  // (NO se sustituye por otra oficina): el origin.store queda vacío y el backend
  // lo rechaza. La validación de fila del Review bloquea antes de llegar aquí.
  const resolveOriginBu = (g: ShipmentGroup) => {
    if (g.originStoreCode) {
      // [F2] Resolver SOLO contra las oficinas origen del contrato. NO sustituir por
      // otra del catálogo global `businessUnits`, que incluye oficinas no autorizadas.
      const match = originBusinessUnits.find(
        (b) => b.buCode === g.originStoreCode
      )
      if (match) return match
      // Oficina origen NO autorizada por el contrato. Con UNA sola oficina no hay
      // nada que elegir: se manda ESA y el Review avisa arriba con un aviso general
      // ("…todas las guías estarán bajo X"). Antes se devolvía undefined también acá,
      // y el payload salía con `origin.store: ''` + `origin.location: ''`: el backend
      // no lo tomaba como "ausente" (`store is None` es falso para `''`) y respondía
      // `location.Location.1` con la lista de códigos VACÍA — un error imposible de
      // interpretar. Al reabrir la fila en Corregir errores el `''` se leía como
      // `undefined` y la guía pasaba sola, así que el error solo aparecía en la carga.
      if (originBusinessUnits.length === 1) return originBusinessUnits[0]
      // Con VARIAS oficinas sí hay algo que elegir: se devuelve undefined a propósito.
      // `validateGroup` marca la fila en rojo y el Review bloquea el envío, así que
      // este payload no llega a salir.
      return undefined
    }
    if (originBusinessUnits.length === 1) return originBusinessUnits[0]
    return user.client?.businessUnit
  }

  const shipment_list = gs.map((g) => {
    const isHome = g.deliveryType === DeliveryEnum.DELIVERY
    const originBu = resolveOriginBu(g)
    const destBu = businessUnits.find((b) => b.buCode === g.storeCode)
    // cityBuCode es el buCode que citiesByBu (Django, /api/location →
    // default_destination_store) asigna a la ciudad: el store de DESTINO para domicilio.
    // El origen no sale de aquí (viene de user.client.businessUnit). NO cruzarlo contra
    // businessUnits: el cruce daba undefined → store vacío en toda guía a domicilio.
    const cityBuCode = citiesByBu.find(
      (c) => c.locationCode === g.destinationCity
    )?.buCode

    const shipperAbbr = (
      identificationTypes.find(
        (t) => t.taxIdentificationTypeId.toString() === g.shipperDocType
      )?.abbreviationName || ''
    ).replace(/-+$/, '')
    const consigneeAbbr = (
      identificationTypes.find(
        (t) => t.taxIdentificationTypeId.toString() === g.consigneeDocType
      )?.abbreviationName || ''
    ).replace(/-+$/, '')

    return {
      origin: {
        home_pickup: false,
        // `null`, NO `''`: el backend distingue ausente (`is None` → error legible
        // "falta tienda o localidad") de presente-pero-inválido. Con `''` el DTO lo
        // daba por presente y el rechazo llegaba como `location.Location.1` con la
        // lista de códigos vacía. Mismo motivo para `location`.
        store: originBu?.buCode || null,
        location: originBu?.location?.locationCode || null,
        address: {
          name: null,
          // La dirección de origen debe ser coherente con origin.store (la oficina
          // elegida por fila), NO la dirección fiscal del cliente cuando difieren.
          line:
            originBu?.location?.address || user.client?.fiscalAddress || 'N/A',
          landmark: null,
          postal_code: originBu?.location?.postalCode || null,
        },
      },
      destination: {
        home_delivery: isHome,
        // Domicilio: enviar la tienda que el front YA resolvió y muestra ("Tienda asignada"
        // = g.storeCode, que viene de cobertura por coordenadas, Maps, selección de ciudad o
        // inferencia al importar). Antes se re-derivaba cityBuCode aquí, lo que hacía que el
        // front mostrara una tienda y el API creara otra cuando la cobertura por coordenadas
        // difería de la tienda por ciudad. cityBuCode queda solo como fallback.
        store: isHome
          ? g.storeCode || cityBuCode || ''
          : destBu?.buCode || g.storeCode,
        location: isHome
          ? g.destinationCity || ''
          : destBu?.location?.locationCode || '',
        ...(isHome && g.latitude != null && g.longitude != null
          ? {
              coordinates: {
                latitude: g.latitude,
                longitude: g.longitude,
              },
            }
          : {}),
        address: {
          name: null,
          line: g.destinationAddress || destBu?.location?.address || '',
          landmark: null,
          postal_code: g.postalCode || null,
        },
      },
      shipper: {
        identification: `${shipperAbbr}-${g.shipperDocNumber}`,
        name: g.shipperName,
        // El código del catálogo puede traer el área pegada ("+1 809"), y el
        // backend exige UN solo espacio: `composeAccountPhone` deja el prefijo
        // como primer token y manda el resto al número.
        phone: composeAccountPhone(
          user.client?.listAccountPhone?.[0]?.countryPhoneAccessCode,
          user.client?.listAccountPhone?.[0]?.phoneNumber,
        ),
        email: user.client?.listAccountEmail?.[0]?.email || undefined,
      },
      consignee: {
        identification: `${consigneeAbbr}-${g.consigneeDocNumber}`,
        name: g.consigneeName,
        // El país del DESTINATARIO es el de su fila, no el de la cuenta del
        // remitente (que es lo que se mandaba antes): un cliente venezolano
        // puede enviarle a un destinatario en Colombia.
        phone: composeConsigneePhone(
          g.consigneePhone,
          consigneeCountryCode(g),
        ),
        email: g.consigneeEmail || undefined,
      },
      billing: {
        service: g.serviceId ?? fallbackService,
        payment_mode: parseInt(paymentMode),
        account_code: user.client?.accountCode || '',
      },
      declared_value: {
        amount: g.pieces.reduce(
          (acc, p) => acc + (p.declaredValue || 0) * (p.quantity || 1),
          0
        ),
        currency: 'USD',
        invoice_number: null,
      },
      notes: g.notes || null,
      partner_shipment_id: g.shipmentHeaderReference || null,
      partner_manifest_id: g.externalManifest?.trim() || externalManifestValue,
      pct_discount: null,
      parcels: {
        dimension_unit: 'cm',
        weight_unit: 'kg',
        items: g.pieces.flatMap((p) =>
          Array.from({ length: p.quantity || 1 }, () => ({
            height: p.height || 1,
            width: p.width || 1,
            length: p.length || 1,
            weight: p.weight || 1,
            content_category: parseInt(p.categoryId) || 49,
            content_description: p.productName || null,
          }))
        ),
      },
    }
  })

  return { shipment_list }
}

// ---------------------------------------------------------------------------
// Inverso: payload_json (ShipmentInsertRequestDTO) → ShipmentGroup (Approach A)
// ---------------------------------------------------------------------------

export interface ReverseShipmentContext {
  identificationTypes: any[]
  isCOD?: boolean
}

/** "V-12345678" → { docType: taxIdentificationTypeId, docNumber }. */
function parseIdentification(
  identification: string | undefined,
  identificationTypes: any[]
): { docType: string; docNumber: string } {
  const raw = (identification || '').trim()
  const dash = raw.indexOf('-')
  if (dash < 0) return { docType: '', docNumber: raw }
  const prefix = raw.slice(0, dash).trim()
  const docNumber = raw.slice(dash + 1).trim()
  const id = taxIdTypeIdFromAbbreviation(prefix, identificationTypes)
  return { docType: id ? id.toString() : '', docNumber }
}

/**
 * Reconstruye un ShipmentGroup desde el payload_json serializado de un detalle con
 * error, para reabrirlo en el editor (Review) y corregirlo (Approach A, eng-review E2).
 *
 * LOSSY (documentado en buildShipmentList): el forward expande las piezas por cantidad
 * y colapsa el valor declarado en un agregado. El inverso re-agrupa piezas idénticas y
 * distribuye el agregado uniformemente (declaredValue = amount / totalItems) para que el
 * total al re-enviar coincida; el valor declarado por pieza individual NO es recuperable.
 * `productCode` tampoco viaja en los items → queda vacío.
 */
export function payloadJsonToShipmentGroup(
  payloadJson: string,
  ctx: ReverseShipmentContext
): ShipmentGroup {
  const { identificationTypes, isCOD = false } = ctx
  const p = JSON.parse(payloadJson)

  const isHome = !!p?.destination?.home_delivery
  const shipper = parseIdentification(
    p?.shipper?.identification,
    identificationTypes
  )
  const consignee = parseIdentification(
    p?.consignee?.identification,
    identificationTypes
  )

  // Teléfono: el forward guardó `${countryCode} ${numero}`. Se conservan LAS DOS
  // partes — antes se descartaba el código y una guía colombiana volvía al editor
  // como venezolana, con el número marcado en rojo y sin forma de arreglarlo.
  // Sin código reconocible (payload viejo o valor a medio escribir) vale la
  // convención de siempre: Venezuela.
  const rawPhone = (p?.consignee?.phone || '').trim()
  const phoneMatch = rawPhone.match(/^(\+\d{1,3})\s+(.+)$/)
  const consigneePhoneCountryCode = phoneMatch
    ? phoneMatch[1]
    : PHONE_COUNTRY_CODE
  const consigneePhoneRaw = phoneMatch
    ? phoneMatch[2].replace(/\s+/g, '')
    : rawPhone
  // Las guías creadas antes del cambio de convención traen el fijo con el 0 de
  // marcación nacional ("+58 02121234567"). Al reabrirlas para corregir se
  // normalizan a los 10 dígitos de hoy; si no, la fila entraría marcada en rojo
  // por un formato que en su momento fue el correcto.
  const consigneePhone =
    consigneePhoneCountryCode === PHONE_COUNTRY_CODE
      ? normalizePhone(consigneePhoneRaw)
      : consigneePhoneRaw

  // Re-agrupar items idénticos → piezas con cantidad.
  const items: any[] = Array.isArray(p?.parcels?.items) ? p.parcels.items : []
  const totalItems = items.length
  const amount = Number(p?.declared_value?.amount) || 0
  const perItemValue = totalItems > 0 ? amount / totalItems : 0

  const pieceMap = new Map<string, ShipmentPieceRow>()
  for (const it of items) {
    const key = [
      it?.height,
      it?.width,
      it?.length,
      it?.weight,
      it?.content_category,
      it?.content_description,
    ].join('|')
    const existing = pieceMap.get(key)
    if (existing) {
      existing.quantity += 1
    } else {
      pieceMap.set(key, {
        quantity: 1,
        weight: Number(it?.weight) || 0,
        height: Number(it?.height) || 0,
        width: Number(it?.width) || 0,
        length: Number(it?.length) || 0,
        declaredValue: perItemValue,
        productName: it?.content_description || '',
        productCode: '',
        categoryId:
          it?.content_category != null ? String(it.content_category) : '49',
      })
    }
  }

  return {
    id: uuidv4(),
    shipmentHeaderReference: p?.partner_shipment_id || '',
    deliveryType: isHome ? DeliveryEnum.DELIVERY : DeliveryEnum.OFFICE,
    destinationCity: p?.destination?.location || '',
    storeCode: p?.destination?.store || '',
    // [F3] Preservar el origen elegido al reabrir una fila en corrección.
    originStoreCode: p?.origin?.store || undefined,
    // Idem el servicio: sin esto, corregir una fila reasignaba el primer servicio
    // autorizado y cambiaba en silencio el que se había elegido.
    //
    // El forward lo escribe en `billing.service` (ver buildShipmentList), NO en un
    // `service` de primer nivel: leer solo `p.service` devolvía siempre undefined y el
    // bug se comía el servicio elegido en cada corrección. Se conserva la lectura vieja
    // como fallback por si algún payload histórico se serializó de otra forma.
    serviceId:
      p?.billing?.service != null
        ? Number(p.billing.service)
        : p?.service != null
          ? Number(p.service)
          : undefined,
    shipperDocType: shipper.docType,
    shipperDocNumber: shipper.docNumber,
    shipperName: p?.shipper?.name || '',
    consigneeDocType: consignee.docType,
    consigneeDocNumber: consignee.docNumber,
    consigneeName: p?.consignee?.name || '',
    consigneePhone,
    consigneePhoneCountryCode,
    consigneeEmail: p?.consignee?.email || '',
    destinationAddress: p?.destination?.address?.line || '',
    postalCode: p?.destination?.address?.postal_code || '',
    notes: p?.notes || '',
    latitude: isHome ? (p?.destination?.coordinates?.latitude ?? null) : null,
    longitude: isHome ? (p?.destination?.coordinates?.longitude ?? null) : null,
    pieces: Array.from(pieceMap.values()),
    isShipperLocked: isCOD,
    isDuplicateInDB: false,
    // Mismo desfase que `serviceId`: el forward lo escribe como `partner_manifest_id`.
    // Leer solo `external_manifest` perdía el manifiesto aliado en cada corrección.
    externalManifest:
      p?.partner_manifest_id || p?.external_manifest || undefined,
  }
}

// ---------------------------------------------------------------------------
// Identidad de una fila de carga masiva: el nº de guía aliada
// ---------------------------------------------------------------------------

/**
 * Nº de guía aliada guardado en el `payload_json` de un detalle (`partner_shipment_id`,
 * la clave que escribe `buildShipmentList` acá arriba).
 *
 * Existe porque `shipment_header_reference` del detalle viene VACÍO en las filas que
 * fallaron antes de crear la guía — o sea, justo en las que el operador necesita
 * identificar para reportar el error. El dato ya viaja al front dentro del payload.
 *
 * NO usa `payloadJsonToShipmentGroup`: eso reconstruye la guía entera (piezas,
 * catálogos, teléfono) para leer un string, y tira excepción con un payload corrupto.
 * Esta función NUNCA lanza: payload nulo, JSON roto ('{'), 'null', '[]', '"str"',
 * '123' o un id no-string devuelven `null`.
 */
export function partnerIdFromPayload(
  payloadJson: string | null | undefined
): string | null {
  const raw = (payloadJson ?? '').trim()
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  const id = (parsed as { partner_shipment_id?: unknown }).partner_shipment_id
  if (typeof id !== 'string') return null
  return id.trim() || null
}

/**
 * Nº de guía aliada de un detalle: el del propio detalle y, si no lo trae (fila que
 * nunca llegó a crear la guía), el del payload.
 *
 * Una sola definición para la celda de la tabla, el buscador, los exports, el título
 * del modal de payload, la confirmación de borrado y las líneas del log — si dos
 * superficies mostraran ids distintos para la misma fila, el operador no podría
 * reportarla.
 */
export function detailPartnerId(d: ImportDetailDTO): string | null {
  return (
    d.shipment_header_reference?.trim() || partnerIdFromPayload(d.payload_json)
  )
}

// ---------------------------------------------------------------------------
// Campos de impresión de una fila: servicio y oficina origen
// ---------------------------------------------------------------------------

/**
 * Servicio y oficina origen guardados en el `payload_json` de un detalle, para los
 * caminos de impresión que los exigen (hoy solo **Documento Bolsillo**, que va al
 * `.NET DownloadAllDocumentDeclared` con `serviceID` y `bUCodeSource`).
 *
 * OJO — esto es el dato que el front MANDÓ al crear la guía, no necesariamente el que
 * el backend guardó. En particular `origin.store` viene VACÍO a propósito cuando la
 * fila no fijó oficina origen (ver `[F2]` en `resolveOriginBu` acá arriba: el front lo
 * deja en blanco y deja que el backend lo resuelva). Por eso las etiquetas Zebra NO
 * usan esta función: piden el ZPL a Django, que resuelve ambos campos desde la base.
 *
 * Mismo contrato que `partnerIdFromPayload`: NUNCA lanza. Payload nulo, JSON roto
 * ('{'), 'null', '[]', '"str"' o '123' devuelven los dos campos en `null`.
 */
export function printFieldsFromPayload(
  payloadJson: string | null | undefined
): { serviceCode: string | null; originStore: string | null } {
  const empty = { serviceCode: null, originStore: null }
  const raw = (payloadJson ?? '').trim()
  if (!raw) return empty
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return empty
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return empty
  const p = parsed as { billing?: unknown; service?: unknown; origin?: unknown }

  // Mismo desfase que `payloadJsonToShipmentGroup`: el forward escribe el servicio en
  // `billing.service`, no en un `service` de primer nivel. Se conserva la lectura vieja
  // como fallback por si algún payload histórico se serializó de otra forma.
  const billing =
    p.billing && typeof p.billing === 'object' && !Array.isArray(p.billing)
      ? (p.billing as { service?: unknown })
      : null
  const rawService = billing?.service ?? p.service
  const serviceCode =
    typeof rawService === 'number' && Number.isFinite(rawService)
      ? String(rawService)
      : typeof rawService === 'string' && rawService.trim()
        ? rawService.trim()
        : null

  const origin =
    p.origin && typeof p.origin === 'object' && !Array.isArray(p.origin)
      ? (p.origin as { store?: unknown })
      : null
  const originStore =
    typeof origin?.store === 'string' && origin.store.trim()
      ? origin.store.trim()
      : null

  return { serviceCode, originStore }
}
